import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DownloadIcon from '@mui/icons-material/Download'
import PaidIcon from '@mui/icons-material/Paid'
import SendIcon from '@mui/icons-material/Send'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import RequestQuoteIcon from '@mui/icons-material/RequestQuote'
import UndoIcon from '@mui/icons-material/Undo'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import OrganisationSelector from '../NewBooking/OrganisationSelector'
import API from '../../../services/API'
import downloadFile from '../../../helpers/downloadFile'
import { GlobalContext } from '../../../App'
import { currencySymbol, vatMultiplierForItem } from '../../../helpers/region'
import { useNavigate } from 'react-router-dom'
import { QuoteStatusChip } from '../components/quoteStatus'

const invoiceStatuses = [
  'Draft',
  'Approved',
  'Sent',
  'Partially Paid',
  'Paid',
  'Overdue',
]

// Mirrors the server-side rule in routers/patientTransport/invoices.js: a
// Draft invoice can only move to Draft or Approved — everything downstream
// requires having been approved first.
const isStatusOptionDisabled = (currentStatus, optionStatus) =>
  (currentStatus || 'Draft') === 'Draft' && !['Draft', 'Approved'].includes(optionStatus)

const STATUS_COLORS = {
  Draft: 'default',
  Approved: 'info',
  Sent: 'info',
  'Partially Paid': 'warning',
  Paid: 'success',
  Overdue: 'error',
}

// `vatMultiplier` is really the tenant REGION here - renamed at the top
// (standardVatMultiplier → region) so every total handles none/standard/
// reduced and legacy numeric strings the same way the server does
const getLineItemsTotal = (items = [], region) =>
  items.reduce(
    (total, item) =>
      total +
      (Number(item.quantity) || 0) *
        (Number(item.unitPrice) || 0) *
        vatMultiplierForItem(item.vat, region),
    0,
  )

const getInvoiceTotal = (invoice, vatMultiplier) =>
  getLineItemsTotal(invoice?.items, vatMultiplier)

const getInvoiceCredited = (invoice, vatMultiplier) =>
  (invoice?.creditNotes || []).reduce(
    (total, note) => total + getLineItemsTotal(note.items, vatMultiplier),
    0,
  )

const getInvoicePaid = (invoice) =>
  (invoice?.payments || []).reduce(
    (total, payment) => total + (Number(payment.amount) || 0),
    0,
  )

const getCustomerName = (invoice) =>
  invoice.customerType === 'Patient'
    ? invoice.patientCustomer?.name || 'Patient'
    : invoice.organisation?.orgName || '—'

// Full credits the invoice as it stands (minus prior credits); part lets the
// coordinator pick which lines/quantities to credit back.
const CreditNoteDialog = ({ invoice, currency, vatMultiplier, onClose, onIssued }) => {
  const [type, setType] = useState('full')
  const [items, setItems] = useState([])
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!invoice) return
    setType('full')
    setItems((invoice.items || []).map((item) => ({ ...item })))
    setReason('')
    setError('')
  }, [invoice])

  if (!invoice) return null

  const alreadyCredited = getInvoiceCredited(invoice, vatMultiplier)
  const invoiceTotal = getInvoiceTotal(invoice, vatMultiplier)
  // Net of any flat discount, matching the server's credit cap
  const remainingCreditable = Math.max(
    0,
    invoiceTotal - alreadyCredited - (Number(invoice.discount) || 0),
  )
  const creditTotal =
    type === 'full' ? remainingCreditable : getLineItemsTotal(items, vatMultiplier)

  const setLine = (index, field, value) =>
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    )

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const res = await API.post(`/pts/invoices/${invoice._id}/credit-notes`, {
        type,
        items: type === 'part' ? items : undefined,
        reason,
      })
      if (res.status === 201) {
        onIssued?.()
        onClose?.()
      } else {
        setError(res?.data?.error || 'Unable to issue credit note.')
      }
    } catch (e) {
      setError('Unable to issue credit note. Please try again later.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={!!invoice} onClose={busy ? null : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Credit note — {invoice.reference}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} pt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            {currency}
            {remainingCreditable.toFixed(2)} left to credit on this invoice
            {alreadyCredited > 0
              ? ` (${currency}${alreadyCredited.toFixed(2)} already credited).`
              : '.'}
          </Typography>
          <RadioGroup
            row
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <FormControlLabel value="full" control={<Radio />} label="Full credit" />
            <FormControlLabel value="part" control={<Radio />} label="Partial credit" />
          </RadioGroup>
          {type === 'part' && (
            <Stack spacing={1}>
              {items.map((item, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label="Description"
                    size="small"
                    fullWidth
                    value={item.description}
                    onChange={(e) => setLine(index, 'description', e.target.value)}
                  />
                  <TextField
                    label="Qty"
                    type="number"
                    size="small"
                    value={item.quantity}
                    onChange={(e) => setLine(index, 'quantity', e.target.value)}
                    sx={{ width: 90 }}
                  />
                  <TextField
                    label={`Unit (${currency})`}
                    type="number"
                    size="small"
                    value={item.unitPrice}
                    onChange={(e) => setLine(index, 'unitPrice', e.target.value)}
                    sx={{ width: 120 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setItems((prev) => [
                    ...prev,
                    { description: '', quantity: 1, unitPrice: 0, vat: 'none' },
                  ])
                }
              >
                Add line
              </Button>
            </Stack>
          )}
          <TextField
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Divider />
          <Typography sx={{ fontWeight: 800 }}>
            Credit total: {currency}
            {creditTotal.toFixed(2)}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<UndoIcon />}
          disabled={busy || creditTotal <= 0}
          onClick={submit}
        >
          {busy ? 'Issuing...' : 'Issue Credit Note'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const InvoicesView = ({ currency, vatMultiplier }) => {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')
  const [organisation, setOrganisation] = useState(null)
  const [organisations, setOrganisations] = useState([])
  const [selection, setSelection] = useState([])
  const [creditNoteInvoice, setCreditNoteInvoice] = useState(null)

  useEffect(() => {
    API.get('/pts/organisationoptions?type=PTS')
      .then((res) => {
        if (res.status === 200) setOrganisations(res.data || [])
      })
      .catch(() => {})
  }, [])

  const loadInvoices = () => {
    setLoading(true)
    setError('')
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (organisation?._id) params.set('organisation', organisation._id)
    API.get(`/pts/invoices?${params.toString()}`)
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setInvoices(res.data)
        } else {
          setInvoices([])
          setError(res?.data?.error || 'Unable to load invoices.')
        }
      })
      .catch(() => setError('Unable to load invoices.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadInvoices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, organisation])

  // API.js sets validateStatus: () => true, so axios never rejects on
  // 4xx/5xx — only real network failures land in catch. Every call here
  // must check res.status explicitly or a rejected request fails silently.
  const updateStatus = async (invoiceID, nextStatus) => {
    try {
      const res = await API.put(`/pts/invoices/${invoiceID}/status`, { status: nextStatus })
      if (res.status === 200) {
        setError('')
        loadInvoices()
      } else {
        setError(res?.data?.error || 'Unable to update invoice status.')
      }
    } catch (e) {
      setError('Unable to update invoice status. Please try again later.')
    }
  }

  const markPaid = async (invoiceID) => {
    try {
      const res = await API.post(`/pts/invoices/${invoiceID}/mark-paid`, {
        date: moment().format('YYYY-MM-DD'),
      })
      if (res.status === 200) {
        setError('')
        loadInvoices()
      } else {
        setError(res?.data?.error || 'Unable to mark invoice as paid.')
      }
    } catch (e) {
      setError('Unable to mark invoice as paid. Please try again later.')
    }
  }

  const sendInvoice = async (invoiceID) => {
    try {
      const res = await API.post(`/pts/invoices/${invoiceID}/send`)
      if (res.status === 200) {
        setError('')
        loadInvoices()
      } else {
        setError(res?.data?.error || 'Unable to send invoice.')
      }
    } catch (e) {
      setError('Unable to send invoice. Please try again later.')
    }
  }

  const exportToXero = async (ids) => {
    if (!ids.length) return
    // window.open carries no X-Tenant-Subdomain header, so the API can't
    // resolve the tenant in production - download through axios instead
    const result = await downloadFile(
      `/pts/invoices/xero-export?ids=${ids.join(',')}`,
      `xero-pts-invoices-${moment().format('YYYYMMDD')}.csv`,
    )
    if (!result.ok) {
      setError(result.error)
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <TextField
          select
          label="Status"
          size="small"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {invoiceStatuses.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <OrganisationSelector
          value={organisation}
          options={organisations}
          onChange={setOrganisation}
          showAddButton={false}
          hideHelperText
          sx={{ minWidth: 240 }}
          size="small"
        />
        <Box flex={1} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          disabled={!selection.length}
          onClick={() => exportToXero(selection)}
        >
          {selection.length
            ? `Export ${selection.length} to Xero (CSV)`
            : 'Select invoices to export'}
        </Button>
      </Stack>
      <CustomDataGrid
        rows={invoices}
        loading={loading}
        getRowId={(row) => row._id}
        checkboxSelection
        onRowSelectionModelChange={(model) => setSelection(model)}
        autoHeight
        disableRowSelectionOnClick
        localeText={{ noRowsLabel: 'No invoices found' }}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50, 100]}
        columns={[
          { field: 'reference', headerName: 'Reference', minWidth: 140 },
          {
            field: 'organisation',
            headerName: 'Customer',
            flex: 1,
            minWidth: 160,
            valueGetter: (value, row) => getCustomerName(row),
          },
          {
            field: 'status',
            headerName: 'Status',
            minWidth: 170,
            renderCell: ({ row }) => (
              <Stack direction="row" alignItems="center" gap={0.5}>
                <TextField
                  select
                  size="small"
                  value={row.status || 'Draft'}
                  onChange={(e) => updateStatus(row._id, e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  {invoiceStatuses.map((s) => (
                    <MenuItem
                      key={s}
                      value={s}
                      disabled={isStatusOptionDisabled(row.status, s)}
                    >
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
                {(row.status || 'Draft') === 'Draft' && (
                  <Tooltip title="A Draft invoice must be Approved before it can be marked Sent, Partially Paid, Paid, or Overdue.">
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                )}
              </Stack>
            ),
          },
          {
            field: 'invoiceDate',
            headerName: 'Date',
            minWidth: 110,
            renderCell: ({ value }) => (value ? moment(value).format('DD/MM/YYYY') : ''),
          },
          {
            field: 'dueDate',
            headerName: 'Due',
            minWidth: 110,
            renderCell: ({ value }) => (value ? moment(value).format('DD/MM/YYYY') : ''),
          },
          {
            field: 'totalAmount',
            headerName: 'Total',
            minWidth: 100,
            renderCell: ({ row }) => `${currency}${getInvoiceTotal(row, vatMultiplier).toFixed(2)}`,
          },
          {
            field: 'creditedAmount',
            headerName: 'Credited',
            minWidth: 100,
            renderCell: ({ row }) => {
              const credited = getInvoiceCredited(row, vatMultiplier)
              return credited > 0 ? `-${currency}${credited.toFixed(2)}` : '—'
            },
          },
          {
            field: 'totalPaid',
            headerName: 'Paid',
            minWidth: 100,
            renderCell: ({ row }) => `${currency}${getInvoicePaid(row).toFixed(2)}`,
          },
          {
            field: 'balance',
            headerName: 'Balance',
            minWidth: 100,
            renderCell: ({ row }) => {
              const balance =
                getInvoiceTotal(row, vatMultiplier) -
                getInvoiceCredited(row, vatMultiplier) -
                getInvoicePaid(row)
              return `${currency}${Math.max(0, balance).toFixed(2)}`
            },
          },
          {
            field: 'actions',
            headerName: '',
            minWidth: 360,
            sortable: false,
            renderCell: ({ row }) => (
              <Stack direction="row" gap={1}>
                <Button size="small" variant="outlined" href={`/invoices/${row._id}`} target="_blank">
                  Open
                </Button>
                <Button size="small" onClick={() => setCreditNoteInvoice(row)}>
                  Credit Note
                </Button>
                {row.status !== 'Draft' && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={() => sendInvoice(row._id)}
                  >
                    {row.sentAt ? 'Resend' : 'Send'}
                  </Button>
                )}
                {/* Drafts must be approved before they can be marked paid -
                    the server rejects it anyway, so don't offer it */}
                {row.status !== 'Paid' && row.status !== 'Draft' && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<PaidIcon />}
                    onClick={() => markPaid(row._id)}
                  >
                    Paid
                  </Button>
                )}
              </Stack>
            ),
          },
        ]}
      />
      <CreditNoteDialog
        invoice={creditNoteInvoice}
        currency={currency}
        vatMultiplier={vatMultiplier}
        onClose={() => setCreditNoteInvoice(null)}
        onIssued={loadInvoices}
      />
    </Stack>
  )
}

const BillingRunsView = ({ currency, onCommitted }) => {
  const [organisation, setOrganisation] = useState(null)
  const [organisations, setOrganisations] = useState([])
  const [periodStart, setPeriodStart] = useState(
    moment().startOf('month').format('YYYY-MM-DD'),
  )
  const [periodEnd, setPeriodEnd] = useState(
    moment().endOf('month').format('YYYY-MM-DD'),
  )
  const [preview, setPreview] = useState(null)
  const [previewing, setPreviewing] = useState(false)
  const [committing, setCommitting] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [detailGroup, setDetailGroup] = useState(null)

  useEffect(() => {
    API.get('/pts/organisationoptions?type=PTS')
      .then((res) => {
        if (res.status === 200) setOrganisations(res.data || [])
      })
      .catch(() => {})
  }, [])

  const runPreview = async () => {
    if (!organisation?._id) {
      setError('Select an organisation first.')
      return
    }
    setPreviewing(true)
    setError('')
    setResult(null)
    try {
      const res = await API.post('/pts/billing-runs', {
        organisation: organisation._id,
        periodStart,
        periodEnd,
        dryRun: true,
      })
      if (res.status === 200) {
        setPreview(res.data)
      } else {
        setError(res.data?.error || 'Unable to preview this billing run.')
        setPreview(null)
      }
    } catch (e) {
      // validateStatus means only network failures land here - no response body
      setError('Unable to preview this billing run. Please try again later.')
      setPreview(null)
    } finally {
      setPreviewing(false)
    }
  }

  const commit = async () => {
    setCommitting(true)
    setError('')
    try {
      const res = await API.post('/pts/billing-runs', {
        organisation: organisation._id,
        periodStart,
        periodEnd,
        dryRun: false,
      })
      if (res.status === 201) {
        setResult(res.data)
        setPreview(null)
        onCommitted?.()
      } else {
        setError(res.data?.error || 'Unable to create invoices for this billing run.')
      }
    } catch (e) {
      // validateStatus means only network failures land here - no response body
      setError('Unable to create invoices for this billing run. Please try again later.')
    } finally {
      setCommitting(false)
    }
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {result && (
        <Alert severity="success" onClose={() => setResult(null)}>
          Created {result.created.length} draft invoice
          {result.created.length === 1 ? '' : 's'} for {result.organisation?.orgName}:{' '}
          {result.created.map((inv) => inv.reference).join(', ')}. Review them on the
          Invoices tab before sending.
        </Alert>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <OrganisationSelector
          value={organisation}
          options={organisations}
          onChange={(value) => {
            setOrganisation(value)
            setPreview(null)
          }}
          showAddButton={false}
          hideHelperText
          sx={{ minWidth: 260 }}
          size="small"
        />
        <TextField
          type="date"
          label="Period start"
          size="small"
          value={periodStart}
          onChange={(e) => setPeriodStart(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          label="Period end"
          size="small"
          value={periodEnd}
          onChange={(e) => setPeriodEnd(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          startIcon={<RequestQuoteIcon />}
          onClick={runPreview}
          disabled={previewing}
        >
          {previewing ? 'Previewing...' : 'Preview'}
        </Button>
      </Stack>

      {preview && (
        <Stack spacing={1.5}>
          <Alert severity="info">
            {preview.candidateJourneyCount} chargeable journey
            {preview.candidateJourneyCount === 1 ? '' : 's'} found, grouped as{' '}
            <strong>{preview.groupingMode}</strong>
            {preview.candidateDutyCount > 0 && (
              <>
                , plus {preview.candidateDutyCount} whole-duty billed dut
                {preview.candidateDutyCount === 1 ? 'y' : 'ies'}
              </>
            )}
            {' — '}
            {preview.invoices.length} draft invoice
            {preview.invoices.length === 1 ? '' : 's'} in total.
          </Alert>
          {preview.invoices.length > 0 && (
            <>
              <CustomDataGrid
                rows={preview.invoices.map((row, index) => ({ ...row, _id: row.groupKey || index }))}
                getRowId={(row) => row._id}
                autoHeight
                disableRowSelectionOnClick
                onRowClick={({ row }) => setDetailGroup(row)}
                slots={{ toolbar: () => null, footer: () => null }}
                columns={[
                  {
                    field: 'patientName',
                    headerName: 'Group',
                    flex: 1,
                    minWidth: 160,
                    valueGetter: (value, row) =>
                      row.dutyLabel ||
                      row.patientName ||
                      `${row.journeyCount} journeys`,
                  },
                  { field: 'customerType', headerName: 'Bill to', minWidth: 110 },
                  { field: 'journeyCount', headerName: 'Journeys', minWidth: 90 },
                  {
                    field: 'subtotal',
                    headerName: 'Subtotal (ex VAT)',
                    minWidth: 140,
                    renderCell: ({ row }) => `${currency}${Number(row.subtotal).toFixed(2)}`,
                  },
                  { field: 'invoiceDate', headerName: 'Invoice date', minWidth: 110 },
                  { field: 'dueDate', headerName: 'Due date', minWidth: 110 },
                ]}
              />
              <Button
                variant="contained"
                startIcon={<ReceiptLongIcon />}
                onClick={commit}
                disabled={committing}
                sx={{ alignSelf: 'flex-start' }}
              >
                {committing
                  ? 'Creating...'
                  : `Create ${preview.invoices.length} Draft Invoice${preview.invoices.length === 1 ? '' : 's'}`}
              </Button>
            </>
          )}
        </Stack>
      )}

      <Dialog open={!!detailGroup} onClose={() => setDetailGroup(null)} fullWidth maxWidth="sm">
        <DialogTitle>Invoice lines</DialogTitle>
        <DialogContent>
          <Stack spacing={0.5} pt={1}>
            {detailGroup?.items?.map((line) => (
              <Typography key={line.id} variant="body2">
                {line.quantity} × {currency}
                {Number(line.unitPrice).toFixed(2)} — {line.description}
                {line.detail ? ` ${line.detail}` : ''}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailGroup(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

// Bookings with no invoice and no accepted quote - the commercial chase
// list. Rows link straight to the booking, where quotes are managed.
const QuotesWorklistView = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    API.get('/pts/quoting-worklist')
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setRows(res.data)
        } else {
          setError(res?.data?.error || 'Unable to load the quoting work-list.')
        }
      })
      .catch(() => setError('Unable to load the quoting work-list.'))
      .finally(() => setLoading(false))
  }, [])

  return (
    <Stack spacing={1.5}>
      {error && <Alert severity="error">{error}</Alert>}
      <Alert severity="info" variant="outlined">
        Bookings without an invoice or an accepted quote. Open a booking to
        generate, send or chase its quote.
      </Alert>
      <CustomDataGrid
        rows={rows}
        loading={loading}
        getRowId={(row) => row._id}
        autoHeight
        disableRowSelectionOnClick
        onRowClick={({ row }) => navigate(`/pts/bookings/${row._id}`)}
        localeText={{
          noRowsLabel: 'Nothing to chase - every booking is invoiced or has an accepted quote',
        }}
        columns={[
          { field: 'bookingRef', headerName: 'Booking', minWidth: 120 },
          {
            field: 'patientName',
            headerName: 'Patient',
            flex: 1,
            minWidth: 140,
            renderCell: ({ row }) => row.patientName || '—',
          },
          {
            field: 'organisationName',
            headerName: 'Customer',
            flex: 1,
            minWidth: 150,
            renderCell: ({ row }) => row.organisationName || 'Individual',
          },
          {
            field: 'firstJourneyTime',
            headerName: 'First Journey',
            minWidth: 140,
            renderCell: ({ row }) =>
              row.firstJourneyTime
                ? moment(row.firstJourneyTime).format('DD/MM/YYYY HH:mm')
                : '—',
          },
          {
            field: 'journeyCount',
            headerName: 'Journeys',
            minWidth: 90,
          },
          {
            field: 'status',
            headerName: 'Booking Status',
            minWidth: 130,
            renderCell: ({ row }) => (
              <Chip
                size="small"
                label={row.status}
                sx={{ borderRadius: 1, fontWeight: 700 }}
              />
            ),
          },
          {
            field: 'quoteStatus',
            headerName: 'Quote',
            minWidth: 140,
            renderCell: ({ row }) => <QuoteStatusChip status={row.quoteStatus} />,
          },
        ]}
      />
    </Stack>
  )
}

const PTSInvoices = () => {
  const { tenantInfo } = useContext(GlobalContext)
  const currency = currencySymbol(tenantInfo?.region)
  // Passed down under the legacy name - the helpers above treat it as the
  // region and resolve each line's own VAT rate
  const vatMultiplier = tenantInfo?.region
  const [view, setView] = useState('Invoices')
  const [refreshKey, setRefreshKey] = useState(0)

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack direction="row" alignItems="center">
          <Box order={{ xs: 2, lg: 1 }} flex={1} display="flex" justifyContent="flex-start">
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(e, value) => value && setView(value)}
              size="small"
              color="contrast"
            >
              <ToggleButton value="Invoices">
                <ReceiptLongIcon fontSize="small" sx={{ mr: 0.5 }} />
                Invoices
              </ToggleButton>
              <ToggleButton value="Billing Runs">
                <PaidIcon fontSize="small" sx={{ mr: 0.5 }} />
                Billing Runs
              </ToggleButton>
              <ToggleButton value="Quotes">
                <RequestQuoteIcon fontSize="small" sx={{ mr: 0.5 }} />
                Quotes
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box display="flex" flex={1} justifyContent="center" order={{ xs: 1, lg: 2 }}>
            <Typography variant="title2">PTS Invoices</Typography>
          </Box>
          <Box order={3} flex={1} />
        </Stack>
      </Grid>
      <Grid item xs={12}>
        {view === 'Invoices' ? (
          <InvoicesView key={refreshKey} currency={currency} vatMultiplier={vatMultiplier} />
        ) : view === 'Billing Runs' ? (
          <BillingRunsView
            currency={currency}
            onCommitted={() => setRefreshKey((k) => k + 1)}
          />
        ) : (
          <QuotesWorklistView />
        )}
      </Grid>
    </Grid>
  )
}

export default PTSInvoices
